/**
 * Craps: two dice, the come-out/point cycle, and the closed v1 bet registry.
 * No cards, no hidden information — the dice and every stake are community
 * property, so the whole game is "what did the dice do, what does that pay".
 *
 * Everything here is a pure function over one roll. The server layer supplies
 * the dice (from its stacked queue in tests, else `room.rng`), the current
 * point and the rules; sequencing, chips and clocks live there, not here.
 */
import type { CrapsRules } from './crapsRules.js';
import type { Rng } from './blackjack.js';

/** One die face. Two of them, always read as a pair. */
export type CrapsRoll = [number, number];

export const rollTotal = (roll: CrapsRoll): number => roll[0] + roll[1];

/** A pair — 3-3, not 4-2. Only the four even totals have a hard way. */
export const isHardRoll = (roll: CrapsRoll): boolean => roll[0] === roll[1];

/** The six numbers that can become the point. */
export const POINT_NUMBERS: readonly number[] = [4, 5, 6, 8, 9, 10];

/** Two dice from the room's rng — the same seam the shoe builds go through. */
export function rollDice(rng: Rng): CrapsRoll {
  return [rng(6) + 1, rng(6) + 1];
}

/**
 * What one roll did to the round, independent of anybody's money:
 *
 * - `natural` — 7 or 11 on the come-out: the pass line wins and the round ends
 * - `craps` — 2, 3 or 12 on the come-out: the pass line loses and the round ends
 * - `point_set` — the puck goes ON and the round keeps rolling
 * - `point_roll` — a number that decided nothing
 * - `point_made` — the point repeated: the round ends, the shooter keeps the dice
 * - `seven_out` — a 7 with the puck ON: the round ends and the dice pass on
 */
export type CrapsRollEvent =
  | 'natural'
  | 'craps'
  | 'point_set'
  | 'point_roll'
  | 'point_made'
  | 'seven_out';

export interface CrapsRollOutcome {
  event: CrapsRollEvent;
  /** The point after this roll: null on the come-out and after a decision. */
  point: number | null;
}

/**
 * Advance the come-out/point cycle by one roll. A come-out decision ends the
 * round too — the line has been settled, so the next come-out deserves a fresh
 * betting window rather than a silent re-roll.
 */
export function advanceCrapsRound(point: number | null, roll: CrapsRoll): CrapsRollOutcome {
  const total = rollTotal(roll);
  if (point === null) {
    if (total === 7 || total === 11) return { event: 'natural', point: null };
    if (total === 2 || total === 3 || total === 12) return { event: 'craps', point: null };
    return { event: 'point_set', point: total };
  }
  if (total === point) return { event: 'point_made', point: null };
  if (total === 7) return { event: 'seven_out', point: null };
  return { event: 'point_roll', point };
}

/** True for the two events that also pass or keep the dice: the round is over. */
export const crapsRoundOver = (event: CrapsRollEvent): boolean => event !== 'point_set' && event !== 'point_roll';

/** Only a seven-out moves the dice along; every other ending keeps them. */
export const crapsDicePass = (event: CrapsRollEvent): boolean => event === 'seven_out';

// --- the bet registry -------------------------------------------------------

export type CrapsBetId =
  | 'pass'
  | 'dontPass'
  | 'passOdds'
  | 'dontOdds'
  | 'place4'
  | 'place5'
  | 'place6'
  | 'place8'
  | 'place9'
  | 'place10'
  | 'field'
  | 'any7'
  | 'anyCraps'
  | 'two'
  | 'three'
  | 'eleven'
  | 'twelve'
  | 'hard4'
  | 'hard6'
  | 'hard8'
  | 'hard10';

/** How the layout is grouped on a phone, and which host switch gates it. */
export type CrapsBetGroup = 'line' | 'odds' | 'place' | 'field' | 'prop' | 'hardway';

/**
 * When a bet may be put down. The line is a come-out contract; odds only
 * exist behind a line bet with the puck ON; everything else goes down on any
 * live roll.
 */
export type CrapsBetWindow = 'come_out' | 'point' | 'any';

/** Chips won per chip staked, as the fraction the felt prints. */
export interface CrapsPayout {
  num: number;
  den: number;
}

export interface CrapsBetDef {
  id: CrapsBetId;
  label: string;
  group: CrapsBetGroup;
  /** How the odds read on the layout: "7:6", "true odds", "1:1". */
  odds: string;
  window: CrapsBetWindow;
  /** The number this bet rides on, for place and hardway bets. */
  number?: number;
  /**
   * True for the bets that sleep through a come-out roll unless their owner
   * calls them working — the place and hardway bets, casino-standard.
   */
  offOnComeOut: boolean;
  /** Fixed paytable entry, or null when the point decides the price (odds). */
  pays: CrapsPayout | null;
}

export const VALID_CRAPS_BETS: readonly CrapsBetId[] = [
  'pass',
  'dontPass',
  'passOdds',
  'dontOdds',
  'place4',
  'place5',
  'place6',
  'place8',
  'place9',
  'place10',
  'field',
  'any7',
  'anyCraps',
  'two',
  'three',
  'eleven',
  'twelve',
  'hard4',
  'hard6',
  'hard8',
  'hard10',
];

/** True odds behind the pass line: the one bet on the table with no edge. */
export const PASS_ODDS_PAYS: Record<number, CrapsPayout> = {
  4: { num: 2, den: 1 },
  10: { num: 2, den: 1 },
  5: { num: 3, den: 2 },
  9: { num: 3, den: 2 },
  6: { num: 6, den: 5 },
  8: { num: 6, den: 5 },
};

/** The lay side: the don't bettor risks more than they win, at the same odds. */
export const DONT_ODDS_PAYS: Record<number, CrapsPayout> = {
  4: { num: 1, den: 2 },
  10: { num: 1, den: 2 },
  5: { num: 2, den: 3 },
  9: { num: 2, den: 3 },
  6: { num: 5, den: 6 },
  8: { num: 5, den: 6 },
};

const PLACE_PAYS: Record<number, CrapsPayout> = {
  4: { num: 9, den: 5 },
  10: { num: 9, den: 5 },
  5: { num: 7, den: 5 },
  9: { num: 7, den: 5 },
  6: { num: 7, den: 6 },
  8: { num: 7, den: 6 },
};

const placeDef = (n: number): CrapsBetDef => {
  const pays = PLACE_PAYS[n]!;
  return {
    id: `place${n}` as CrapsBetId,
    label: `Place ${n}`,
    group: 'place',
    odds: `${pays.num}:${pays.den}`,
    window: 'any',
    number: n,
    offOnComeOut: true,
    pays,
  };
};

const hardDef = (n: number, num: number): CrapsBetDef => ({
  id: `hard${n}` as CrapsBetId,
  label: `Hard ${n}`,
  group: 'hardway',
  odds: `${num}:1`,
  window: 'any',
  number: n,
  offOnComeOut: true,
  pays: { num, den: 1 },
});

const propDef = (id: CrapsBetId, label: string, num: number): CrapsBetDef => ({
  id,
  label,
  group: 'prop',
  odds: `${num}:1`,
  window: 'any',
  offOnComeOut: false,
  pays: { num, den: 1 },
});

export const CRAPS_BETS: Record<CrapsBetId, CrapsBetDef> = {
  pass: {
    id: 'pass',
    label: 'Pass line',
    group: 'line',
    odds: '1:1',
    window: 'come_out',
    offOnComeOut: false,
    pays: { num: 1, den: 1 },
  },
  dontPass: {
    id: 'dontPass',
    label: "Don't pass",
    group: 'line',
    odds: '1:1, bar 12',
    window: 'come_out',
    offOnComeOut: false,
    pays: { num: 1, den: 1 },
  },
  passOdds: {
    id: 'passOdds',
    label: 'Pass odds',
    group: 'odds',
    odds: 'true odds',
    window: 'point',
    offOnComeOut: false,
    pays: null,
  },
  dontOdds: {
    id: 'dontOdds',
    label: 'Lay odds',
    group: 'odds',
    odds: 'true odds',
    window: 'point',
    offOnComeOut: false,
    pays: null,
  },
  place4: placeDef(4),
  place5: placeDef(5),
  place6: placeDef(6),
  place8: placeDef(8),
  place9: placeDef(9),
  place10: placeDef(10),
  field: {
    id: 'field',
    label: 'Field',
    group: 'field',
    odds: '1:1, 2 and 12 double',
    window: 'any',
    offOnComeOut: false,
    pays: { num: 1, den: 1 },
  },
  any7: propDef('any7', 'Any 7', 4),
  anyCraps: propDef('anyCraps', 'Any craps', 7),
  two: propDef('two', 'Snake eyes (2)', 30),
  three: propDef('three', 'Ace-deuce (3)', 15),
  eleven: propDef('eleven', 'Yo (11)', 15),
  twelve: propDef('twelve', 'Boxcars (12)', 30),
  hard4: hardDef(4, 7),
  hard6: hardDef(6, 9),
  hard8: hardDef(8, 9),
  hard10: hardDef(10, 7),
};

/** The bet a line's odds ride behind. */
export const ODDS_BEHIND: Record<'passOdds' | 'dontOdds', 'pass' | 'dontPass'> = {
  passOdds: 'pass',
  dontOdds: 'dontPass',
};

/** Does the host have this bet's group switched on? */
export function crapsBetEnabled(id: CrapsBetId, rules: CrapsRules): boolean {
  const group = CRAPS_BETS[id].group;
  if (group === 'prop') return rules.propsEnabled;
  if (group === 'hardway') return rules.hardwaysEnabled;
  if (group === 'place') return rules.placeEnabled;
  return true;
}

/**
 * Can this bet go down right now? The line closes the moment the puck goes
 * ON — that contract is struck on the come-out — and odds cannot exist before
 * there is a point to price them against.
 */
export function canPlaceCrapsBet(id: CrapsBetId, point: number | null, rules: CrapsRules): boolean {
  if (!crapsBetEnabled(id, rules)) return false;
  const { window } = CRAPS_BETS[id];
  if (window === 'come_out') return point === null;
  if (window === 'point') return point !== null;
  return true;
}

/**
 * Can this bet come back off the felt right now? Everything a player puts
 * down is theirs until they pull it — place bets, hardways, odds, the don't
 * line — with one exception the whole game rests on: a pass line bet with the
 * puck ON is a struck contract and rides to its decision.
 */
export function canPullCrapsBet(id: CrapsBetId, point: number | null): boolean {
  return id !== 'pass' || point === null;
}

/**
 * The most a seat may put behind its line bet. Pass odds are capped at the
 * line times the table multiple; a lay is capped at the stake that *wins*
 * that same amount, since the don't bettor lays the long end of the price.
 */
export function maxCrapsOdds(
  id: 'passOdds' | 'dontOdds',
  lineAmount: number,
  point: number | null,
  rules: CrapsRules,
): number {
  if (point === null || lineAmount <= 0) return 0;
  const cap = lineAmount * rules.maxOddsX;
  if (id === 'passOdds') return cap;
  const pays = DONT_ODDS_PAYS[point];
  if (!pays) return 0;
  return Math.floor((cap * pays.den) / pays.num);
}

/** What the field pays on this total, or null when the field loses. */
export function fieldPayout(total: number, rules: CrapsRules): CrapsPayout | null {
  if (total === 2) return { num: 2, den: 1 };
  if (total === 12) return rules.fieldTriple12 ? { num: 3, den: 1 } : { num: 2, den: 1 };
  if (total === 3 || total === 4 || total === 9 || total === 10 || total === 11) {
    return { num: 1, den: 1 };
  }
  return null;
}

// --- pressing ---------------------------------------------------------------

/**
 * What a bet that survives its own win does with the chips. Only the place
 * and hardway bets have this choice to make — every other spot on the layout
 * is a one-roll bet or a contract that settles and is gone:
 *
 * - `down` — the whole stack comes back, stake and win together
 * - `same` — the win is paid and the stake stays on the number: same bet
 * - `press` — the win goes straight back up on top of the stake
 *
 * `same` is how a real box works: a place bet that hits is still out there on
 * the next roll unless its owner says otherwise. `press` is the same call a
 * player makes out loud — "press the six" — without having to buy the number
 * back a chip at a time.
 */
export type CrapsPressMode = 'down' | 'same' | 'press';

export const CRAPS_PRESS_MODES: readonly CrapsPressMode[] = ['down', 'same', 'press'];

export const isCrapsPressMode = (value: unknown): value is CrapsPressMode =>
  CRAPS_PRESS_MODES.includes(value as CrapsPressMode);

/** Only the bets that ride from roll to roll can be pressed or left up. */
export const crapsCanPress = (id: CrapsBetId): boolean => CRAPS_BETS[id].offOnComeOut;

/** The next mode in the cycle, for the phone's one-tap toggle. */
export const nextCrapsPressMode = (mode: CrapsPressMode): CrapsPressMode =>
  CRAPS_PRESS_MODES[(CRAPS_PRESS_MODES.indexOf(mode) + 1) % CRAPS_PRESS_MODES.length]!;

export interface CrapsPressPlan {
  /** Chips left on the number once the win is paid. */
  rides: number;
  /** Chips handed to the player: whatever the felt did not keep. */
  toChips: number;
}

/**
 * Split a winning payout between the felt and the rail. The table maximum is
 * still the sign over the spot, so a press that would break it presses as far
 * as it can and pays the rest out rather than being refused.
 */
export function crapsPressPlan(
  mode: CrapsPressMode,
  stake: number,
  payout: number,
  maxBet: number,
): CrapsPressPlan {
  if (mode === 'down') return { rides: 0, toChips: payout };
  const target = mode === 'press' ? payout : stake;
  const rides = Math.max(0, Math.min(target, maxBet, payout));
  return { rides, toChips: payout - rides };
}

// --- settlement -------------------------------------------------------------

/**
 * What one roll did to one bet:
 *
 * - `win` — chips come back, stake included
 * - `lose` — the stake is gone
 * - `push` — the stake comes back and the bet comes off (the don't line's bar 12)
 * - `stand` — no decision: the bet rides to the next roll
 */
export type CrapsResolution = 'win' | 'lose' | 'push' | 'stand';

export interface CrapsBetOutcome {
  resolution: CrapsResolution;
  /** Chips returned to the seat: 0 on a loss and while a bet rides. */
  payout: number;
}

/** One stake on the layout, as the engine reads it. */
export interface CrapsBetSpec {
  id: CrapsBetId;
  amount: number;
  /**
   * Only meaningful for the bets that sleep on a come-out roll: true means
   * "call it working" and the bet is live through the come-out too.
   */
  working: boolean;
}

/**
 * Chips returned on a win, stake included. Winners round up to whole chips:
 * a 5-chip place bet on the 6 pays 6, not 5.83. The house is a living room.
 */
const winReturn = (amount: number, pays: CrapsPayout): number =>
  amount + Math.ceil((amount * pays.num) / pays.den);

const won = (amount: number, pays: CrapsPayout): CrapsBetOutcome => ({
  resolution: 'win',
  payout: winReturn(amount, pays),
});

const lost: CrapsBetOutcome = { resolution: 'lose', payout: 0 };
const rides: CrapsBetOutcome = { resolution: 'stand', payout: 0 };

/**
 * Is this bet awake for this roll? Place and hardway bets sleep through a
 * come-out unless their owner called them working; everything else is always
 * live while it is on the felt.
 */
export function crapsBetWorking(bet: CrapsBetSpec, point: number | null): boolean {
  const def = CRAPS_BETS[bet.id];
  if (!def.offOnComeOut) return true;
  return point !== null || bet.working;
}

/**
 * Settle one bet against one roll. `point` is the point *before* the roll —
 * the state the bet was made under — so a pass line bet reads its come-out
 * matrix exactly once, on the roll that establishes the point.
 */
export function resolveCrapsBet(
  bet: CrapsBetSpec,
  roll: CrapsRoll,
  point: number | null,
  rules: CrapsRules,
): CrapsBetOutcome {
  const def = CRAPS_BETS[bet.id];
  const total = rollTotal(roll);
  if (!crapsBetWorking(bet, point)) return rides;

  switch (def.group) {
    case 'line':
      return resolveLine(bet, def, total, point);
    case 'odds':
      return resolveOdds(bet, total, point);
    case 'place': {
      const n = def.number!;
      if (total === n) return won(bet.amount, def.pays!);
      if (total === 7) return lost;
      return rides;
    }
    case 'field': {
      const pays = fieldPayout(total, rules);
      return pays ? won(bet.amount, pays) : lost;
    }
    case 'hardway': {
      const n = def.number!;
      if (total === n) return isHardRoll(roll) ? won(bet.amount, def.pays!) : lost;
      // Any 7 takes every hardway down with it, the same as it takes the place bets.
      return total === 7 ? lost : rides;
    }
    case 'prop':
      return resolveProp(bet, roll, total);
  }
}

function resolveLine(
  bet: CrapsBetSpec,
  def: CrapsBetDef,
  total: number,
  point: number | null,
): CrapsBetOutcome {
  if (point === null) {
    if (def.id === 'pass') {
      if (total === 7 || total === 11) return won(bet.amount, def.pays!);
      if (total === 2 || total === 3 || total === 12) return lost;
      return rides;
    }
    if (total === 2 || total === 3) return won(bet.amount, def.pays!);
    if (total === 7 || total === 11) return lost;
    // Bar 12: the don't bettor neither wins nor loses. The stake comes straight
    // back rather than riding, so the money on the felt always means something.
    if (total === 12) return { resolution: 'push', payout: bet.amount };
    return rides;
  }
  if (def.id === 'pass') {
    if (total === point) return won(bet.amount, def.pays!);
    if (total === 7) return lost;
    return rides;
  }
  if (total === 7) return won(bet.amount, def.pays!);
  if (total === point) return lost;
  return rides;
}

function resolveOdds(bet: CrapsBetSpec, total: number, point: number | null): CrapsBetOutcome {
  // Odds only ever sit behind a point; the server refuses them otherwise.
  if (point === null) return rides;
  if (bet.id === 'passOdds') {
    if (total === point) return won(bet.amount, PASS_ODDS_PAYS[point]!);
    if (total === 7) return lost;
    return rides;
  }
  if (total === 7) return won(bet.amount, DONT_ODDS_PAYS[point]!);
  if (total === point) return lost;
  return rides;
}

function resolveProp(bet: CrapsBetSpec, roll: CrapsRoll, total: number): CrapsBetOutcome {
  const def = CRAPS_BETS[bet.id];
  const hit =
    bet.id === 'any7'
      ? total === 7
      : bet.id === 'anyCraps'
        ? total === 2 || total === 3 || total === 12
        : bet.id === 'two'
          ? total === 2
          : bet.id === 'three'
            ? total === 3
            : bet.id === 'eleven'
              ? total === 11
              : total === 12;
  void roll;
  return hit ? won(bet.amount, def.pays!) : lost;
}

/** Per-bet outcomes for one roll, in the order the bets arrive. */
export function resolveCrapsBets(
  bets: readonly CrapsBetSpec[],
  roll: CrapsRoll,
  point: number | null,
  rules: CrapsRules,
): CrapsBetOutcome[] {
  return bets.map((bet) => resolveCrapsBet(bet, roll, point, rules));
}

/** How a stake reads on a phone or a TV: "Pass line", "Place 6", "Hard 8". */
export const describeCrapsBet = (id: CrapsBetId): string => CRAPS_BETS[id].label;

/**
 * The bets a come-out roll landed on while they were off: a parked hard 8 on a
 * 4-4 come-out, a parked place 6 when the 6 becomes the point. Nothing settles,
 * so without a call-out the roll looks like it forgot to pay. Deduplicated
 * across seats, in layout order.
 */
export function crapsOffBetsHit(
  bets: readonly Pick<CrapsBetSpec, 'id' | 'working'>[],
  roll: CrapsRoll,
  event: CrapsRollEvent,
): CrapsBetId[] {
  // Every total a sleeping bet rides on sets the point on a come-out.
  if (event !== 'point_set') return [];
  const total = rollTotal(roll);
  const hit = new Set<CrapsBetId>();
  for (const bet of bets) {
    const def = CRAPS_BETS[bet.id];
    if (!def.offOnComeOut || bet.working || def.number !== total) continue;
    if (def.group === 'hardway' && !isHardRoll(roll)) continue;
    hit.add(bet.id);
  }
  return VALID_CRAPS_BETS.filter((id) => hit.has(id));
}

/**
 * How one roll reads out loud: "Seven out", "Point is 6", "Yo eleven". Pass the
 * bets it landed on while they were off and the call says so.
 */
export function describeCrapsRoll(
  roll: CrapsRoll,
  event: CrapsRollEvent,
  offBets: readonly CrapsBetId[] = [],
): string {
  const call = rollCall(roll, event);
  if (offBets.length === 0) return call;
  const labels = offBets.map((id) => CRAPS_BETS[id].label);
  const list =
    labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`;
  return `${call} ${list} ${labels.length === 1 ? 'was' : 'were'} off — no action.`;
}

function rollCall(roll: CrapsRoll, event: CrapsRollEvent): string {
  const total = rollTotal(roll);
  switch (event) {
    case 'natural':
      return total === 7 ? 'Seven — a winner!' : 'Yo eleven!';
    case 'craps':
      return total === 12 ? 'Boxcars — craps.' : `Craps, ${total}.`;
    case 'point_set':
      return `The point is ${total}.`;
    case 'point_made':
      return `${total} — the point is made!`;
    case 'seven_out':
      return 'Seven out. Line away.';
    case 'point_roll':
      return isHardRoll(roll) ? `${total} the hard way.` : `${total}.`;
  }
}
